import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store/store";
import { useTour } from "../../hooks/ui/tour/useTour";
import TourOverlay from "./atoms/TourOverlay";
import TourTooltip from "./atoms/TourTooltip";
import ConfettiModal from "../TourSteps/ConfettiModal";
import type { TourStep } from "../../data/constants/tour-steps/add_item_steps";

export type SharedColumnsType = {
  title: string;
  type: string;
};

interface CustomTourComponentProps {
  tourSteps: TourStep[];
  tourState: boolean;
  handleStepsEnabled: (value: boolean) => void;
  sharedColumnsTour: SharedColumnsType[];
}

const CustomTourComponent = ({
  tourSteps,
  tourState,
  handleStepsEnabled,
  sharedColumnsTour,
}: CustomTourComponentProps) => {
  const boardName = useSelector((state: RootState) => state.targetBoardName.targetBoardName);
  const [openConfetti, setOpenConfetti] = useState(false);

  const { currentStep, targetRect, isWaiting, isActive, tourCompleted, exitTour } = useTour({
    steps: tourSteps,
    enabled: tourState,
  });

  // Notify parent when tour deactivates (backdrop click or completion)
  const wasActiveRef = useRef(false);
  const handleStepsEnabledRef = useRef(handleStepsEnabled);
  handleStepsEnabledRef.current = handleStepsEnabled;

  useEffect(() => {
    if (wasActiveRef.current && !isActive) {
      handleStepsEnabledRef.current(false);
    }
    wasActiveRef.current = isActive;
  }, [isActive]);

  // Picking a group is the tour's last step and shares the item straight away, so the
  // confetti follows completion directly.
  useEffect(() => {
    if (tourCompleted) setOpenConfetti(true);
  }, [tourCompleted]);

  const handleBackdropClick = () => exitTour();
  const handleCloseTour = () => setOpenConfetti(false);

  const currentTourStep = tourSteps[currentStep];
  const showTour = isActive && !isWaiting && targetRect;

  return (
    <>
      {showTour && (
        <>
          <TourOverlay targetRect={targetRect} onBackdropClick={handleBackdropClick} />
          <TourTooltip
            targetRect={targetRect}
            text={currentTourStep?.intro ?? ""}
            stepNumber={currentStep + 1}
            totalSteps={tourSteps.length}
          />
        </>
      )}

      <ConfettiModal
        boardName={boardName}
        tourComplete={openConfetti}
        handleCloseTour={handleCloseTour}
        sharedColumnsTour={sharedColumnsTour}
      />
    </>
  );
};

export default CustomTourComponent;
